import { otsu } from "./threshold";
import type { Pixels, Rect, Stroke } from "./types";

// Stored with each master image and part of the render hash. Only version 1 exists: a change
// to the result for the same inputs needs a new version, and computeMask then has to keep
// producing version 1 for master images that store it.
export const MASK_VERSION = 1;
export const DEFAULT_TOLERANCE = 35;
const RING_RATIO = 0.06;
// Pixels within this many times the joint's own spread of the joint colour count as joint
// when a snap stroke finds no edge.
const JOINT_SPREAD_FACTOR = 3;
const MIN_JOINT_LIMIT = 12;
// Snap-made spots up to a third of the brush radius across count as specks.
const SPECK_RADIUS_RATIO = 3;

export interface MaskInput {
  image: Pixels;
  rect: Rect;
  tolerance: number;
  strokes: Stroke[];
}

type Colour = [number, number, number];

const medianColour = (data: Uint8ClampedArray, indices: number[]): Colour => {
  const colour: Colour = [0, 0, 0];
  for (let c = 0; c < 3; c++) {
    const histogram = new Uint32Array(256);
    for (const i of indices) histogram[data[i * 4 + c]]++;
    let seen = 0;
    for (let v = 0; v < 256; v++) {
      seen += histogram[v];
      if (seen * 2 >= indices.length) {
        colour[c] = v;
        break;
      }
    }
  }
  return colour;
};

const distanceTo = (data: Uint8ClampedArray, i: number, c: Colour) =>
  Math.hypot(data[i * 4] - c[0], data[i * 4 + 1] - c[1], data[i * 4 + 2] - c[2]);

const keepLargestBlob = (solid: Uint8Array, w: number) => {
  const label = new Int32Array(solid.length);
  const queue = new Int32Array(solid.length);
  let best = 0;
  let bestSize = 0;
  let next = 0;
  for (let start = 0; start < solid.length; start++) {
    if (!solid[start] || label[start]) continue;
    next++;
    let tail = 0;
    queue[tail++] = start;
    label[start] = next;
    const visit = (q: number) => {
      if (solid[q] && !label[q]) {
        label[q] = next;
        queue[tail++] = q;
      }
    };
    for (let head = 0; head < tail; head++) {
      const p = queue[head];
      const x = p % w;
      if (x > 0) visit(p - 1);
      if (x < w - 1) visit(p + 1);
      if (p >= w) visit(p - w);
      if (p + w < solid.length) visit(p + w);
    }
    if (tail > bestSize) {
      bestSize = tail;
      best = next;
    }
  }
  for (let i = 0; i < solid.length; i++) solid[i] = best > 0 && label[i] === best ? 1 : 0;
};

const fillHoles = (solid: Uint8Array, w: number, h: number) => {
  const outside = new Uint8Array(solid.length);
  const queue = new Int32Array(solid.length);
  let tail = 0;
  const visit = (q: number) => {
    if (!solid[q] && !outside[q]) {
      outside[q] = 1;
      queue[tail++] = q;
    }
  };
  for (let x = 0; x < w; x++) {
    visit(x);
    visit((h - 1) * w + x);
  }
  for (let y = 0; y < h; y++) {
    visit(y * w);
    visit(y * w + w - 1);
  }
  for (let head = 0; head < tail; head++) {
    const p = queue[head];
    const x = p % w;
    if (x > 0) visit(p - 1);
    if (x < w - 1) visit(p + 1);
    if (p >= w) visit(p - w);
    if (p + w < solid.length) visit(p + w);
  }
  for (let i = 0; i < solid.length; i++) if (!outside[i]) solid[i] = 1;
};

type Stamp = (cx: number, cy: number) => void;

const stamp = (
  alpha: Uint8Array,
  snapped: Uint8Array,
  w: number,
  h: number,
  cx: number,
  cy: number,
  r: number,
  value: number
) => {
  for (let y = Math.max(0, Math.ceil(cy - r)); y <= Math.min(h - 1, Math.floor(cy + r)); y++) {
    for (let x = Math.max(0, Math.ceil(cx - r)); x <= Math.min(w - 1, Math.floor(cx + r)); x++) {
      if ((x - cx) ** 2 + (y - cy) ** 2 <= r * r) {
        alpha[y * w + x] = value;
        snapped[y * w + x] = 0;
      }
    }
  }
};

const circle = (w: number, h: number, cx: number, cy: number, r: number) => {
  const pixels: [number, number][] = [];
  for (let y = Math.max(0, Math.ceil(cy - r)); y <= Math.min(h - 1, Math.floor(cy + r)); y++) {
    for (let x = Math.max(0, Math.ceil(cx - r)); x <= Math.min(w - 1, Math.floor(cx + r)); x++) {
      if ((x - cx) ** 2 + (y - cy) ** 2 <= r * r) pixels.push([x, y]);
    }
  }
  return pixels;
};

// Snap sets the outline inside the brush at the colour edge between joint and brick: when the
// brush covers both, the split between them is found from the pixels under it; otherwise each
// pixel is joint if it is within the joint's usual variation of the joint colour.
const snapStamp = (
  alpha: Uint8Array,
  snapped: Uint8Array,
  w: number,
  h: number,
  r: number,
  jointDistance: (x: number, y: number) => number,
  jointLimit: number
): Stamp => (cx, cy) => {
  const pixels = circle(w, h, cx, cy, r).filter(([x, y]) => !Number.isNaN(jointDistance(x, y)));
  if (pixels.length === 0) return;
  const distances = pixels.map(([x, y]) => jointDistance(x, y));
  const split = otsu(distances);
  const low = distances.filter((d) => d <= split);
  const high = distances.filter((d) => d > split);
  const mean = (values: number[]) => values.reduce((sum, v) => sum + v, 0) / values.length;
  const edge = low.length > 0 && high.length > 0 && mean(low) <= jointLimit && mean(high) > 2 * jointLimit;
  const cutoff = edge ? split : jointLimit;
  pixels.forEach(([x, y], k) => {
    alpha[y * w + x] = distances[k] <= cutoff ? 0 : 255;
    snapped[y * w + x] = 1;
  });
};

// Snap decides pixel by pixel, so at full resolution it cuts single brick pixels that happen
// to be joint-coloured (and keeps joint pixels that look like brick), which the downscaled
// preview never shows. Small spots made only by snap are undone: holes not connected to the
// region border, and islands apart from the brick. Erased holes, added patches and edge bands
// cut by snap on purpose are kept.
const removeSnapSpecks = (alpha: Uint8Array, snapped: Uint8Array, w: number, h: number, maxSpeck: number) => {
  const label = new Int32Array(alpha.length).fill(-1);
  const queue = new Int32Array(alpha.length);
  const components: { pixels: number[]; border: boolean; snapOnly: boolean; value: number }[] = [];
  for (let start = 0; start < alpha.length; start++) {
    if (label[start] >= 0) continue;
    const value = alpha[start];
    const component = { pixels: [] as number[], border: false, snapOnly: true, value };
    label[start] = components.length;
    let tail = 0;
    queue[tail++] = start;
    for (let head = 0; head < tail; head++) {
      const p = queue[head];
      const x = p % w;
      const y = (p - x) / w;
      component.pixels.push(p);
      if (x === 0 || y === 0 || x === w - 1 || y === h - 1) component.border = true;
      if (!snapped[p]) component.snapOnly = false;
      for (const q of [x > 0 ? p - 1 : -1, x < w - 1 ? p + 1 : -1, y > 0 ? p - w : -1, y < h - 1 ? p + w : -1]) {
        if (q >= 0 && label[q] < 0 && alpha[q] === value) {
          label[q] = components.length;
          queue[tail++] = q;
        }
      }
    }
    components.push(component);
  }
  const opaque = components.filter((c) => c.value === 255);
  const brick = opaque.reduce<(typeof components)[number] | null>(
    (best, c) => (!best || c.pixels.length > best.pixels.length ? c : best),
    null
  );
  for (const c of components) {
    if (!c.snapOnly || c.pixels.length > maxSpeck) continue;
    if (c.value === 0 && !c.border) c.pixels.forEach((p) => (alpha[p] = 255));
    if (c.value === 255 && c !== brick) c.pixels.forEach((p) => (alpha[p] = 0));
  }
};

const applyStrokes = (
  alpha: Uint8Array,
  rect: Rect,
  strokes: Stroke[],
  snap: (r: number, snapped: Uint8Array) => Stamp
) => {
  const { width: w, height: h } = rect;
  const snapped = new Uint8Array(alpha.length);
  for (const { mode, radius, points } of strokes) {
    const paint: Stamp =
      mode === "snap"
        ? snap(radius, snapped)
        : (cx, cy) => stamp(alpha, snapped, w, h, cx, cy, radius, mode === "add" ? 255 : 0);
    const px = (i: number) => points[i * 2] - rect.x;
    const py = (i: number) => points[i * 2 + 1] - rect.y;
    const count = points.length / 2;
    paint(px(0), py(0));
    for (let i = 1; i < count; i++) {
      const length = Math.hypot(px(i) - px(i - 1), py(i) - py(i - 1));
      const steps = Math.max(1, Math.ceil(length / Math.max(0.5, radius / 2)));
      for (let s = 1; s <= steps; s++) {
        const t = s / steps;
        paint(px(i - 1) + (px(i) - px(i - 1)) * t, py(i - 1) + (py(i) - py(i - 1)) * t);
      }
    }
  }
  const snapRadii = strokes.filter((stroke) => stroke.mode === "snap").map((stroke) => stroke.radius);
  if (snapRadii.length > 0) {
    removeSnapSpecks(alpha, snapped, w, h, Math.max(4, Math.PI * (Math.min(...snapRadii) / SPECK_RADIUS_RATIO) ** 2));
  }
};

const feather = (alpha: Uint8Array, w: number, h: number) => {
  const out = alpha.slice();
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let sum = 0;
      let count = 0;
      let min = 255;
      let max = 0;
      for (let ny = Math.max(0, y - 1); ny <= Math.min(h - 1, y + 1); ny++) {
        for (let nx = Math.max(0, x - 1); nx <= Math.min(w - 1, x + 1); nx++) {
          const a = alpha[ny * w + nx];
          sum += a;
          count++;
          if (a < min) min = a;
          if (a > max) max = a;
        }
      }
      if (min !== max) out[y * w + x] = Math.round(sum / count);
    }
  }
  return out;
};

export const computeMask = ({ image, rect, tolerance, strokes }: MaskInput): Uint8Array => {
  const { width: w, height: h } = rect;
  const indexAt = (lx: number, ly: number) => (rect.y + ly) * image.width + rect.x + lx;
  const isOpaque = (i: number) => image.data[i * 4 + 3] >= 128;
  const ringWidth = Math.max(1, Math.round(Math.min(w, h) * RING_RATIO));

  const ring: number[] = [];
  const centre: number[] = [];
  for (let ly = 0; ly < h; ly++) {
    for (let lx = 0; lx < w; lx++) {
      const i = indexAt(lx, ly);
      if (!isOpaque(i)) continue;
      if (lx < ringWidth || ly < ringWidth || lx >= w - ringWidth || ly >= h - ringWidth) {
        ring.push(i);
      } else if (lx >= w / 4 && lx < (3 * w) / 4 && ly >= h / 4 && ly < (3 * h) / 4) {
        centre.push(i);
      }
    }
  }

  const solid = new Uint8Array(w * h);
  const joint = ring.length > 0 ? medianColour(image.data, ring) : null;
  if (!joint || centre.length === 0) {
    solid.fill(1);
  } else {
    const brick = medianColour(image.data, centre);
    const cutoff = (tolerance / 100) * Math.hypot(joint[0] - brick[0], joint[1] - brick[1], joint[2] - brick[2]);
    for (let ly = 0; ly < h; ly++) {
      for (let lx = 0; lx < w; lx++) {
        const i = indexAt(lx, ly);
        solid[ly * w + lx] = isOpaque(i) && distanceTo(image.data, i, joint) > cutoff ? 1 : 0;
      }
    }
    keepLargestBlob(solid, w);
    fillHoles(solid, w, h);
  }

  const alpha = new Uint8Array(w * h);
  for (let i = 0; i < alpha.length; i++) alpha[i] = solid[i] * 255;
  const jointDistance = (x: number, y: number) => {
    const i = indexAt(x, y);
    return joint && isOpaque(i) ? distanceTo(image.data, i, joint) : NaN;
  };
  const ringDistances = joint ? ring.map((i) => distanceTo(image.data, i, joint)).sort((a, b) => a - b) : [];
  const jointLimit = Math.max(
    MIN_JOINT_LIMIT,
    JOINT_SPREAD_FACTOR * (ringDistances[Math.floor(ringDistances.length / 2)] ?? 0)
  );
  applyStrokes(alpha, rect, strokes, (r, snapped) => snapStamp(alpha, snapped, w, h, r, jointDistance, jointLimit));
  return feather(alpha, w, h);
};
